// Sliby: seznam podle filtru, optimistické Splněno s vrácením při chybě, vytvoření, úprava a smazání.
// Změna slibu mění i detail klienta a zakázky: jejich cache se zahodí.
import { defineStore } from 'pinia'
import { ref } from 'vue'
import type { CreatePromiseInput, PromiseItem, UpdatePromiseInput } from '@wac-buddy/shared'
import {
  createPromise,
  deletePromise,
  getPromise,
  listPromises,
  updatePromise,
  type PromiseFilter,
} from '@/api/promises'
import { useClientsStore } from './clients'
import { useJobsStore } from './jobs'

export type ListStatus = 'idle' | 'loading' | 'ready' | 'error'
export type PromiseListFilter = Extract<PromiseFilter, 'open' | 'overdue' | 'today' | 'done'>

export const PROMISE_LIST_FILTERS: PromiseListFilter[] = ['open', 'overdue', 'today', 'done']

const PAGE_SIZE = 50

/** Patří slib se stavem `status` do seznamu s filtrem `filter`? (Termín posuzuje api, tady jen stav.) */
function belongs(filter: PromiseListFilter, status: PromiseItem['status']): boolean {
  return filter === 'done' ? status === 'done' : status === 'open'
}

export const usePromisesStore = defineStore('promises', () => {
  const filter = ref<PromiseListFilter>('open')
  const items = ref<PromiseItem[]>([])
  const nextCursor = ref<string | null>(null)
  const status = ref<ListStatus>('idle')
  const loadingMore = ref(false)

  // Odpovědi, které přijdou po novějším dotazu (rychlé přepínání filtru), se zahodí.
  let requestSeq = 0

  async function load(): Promise<void> {
    const seq = ++requestSeq
    status.value = 'loading'
    try {
      const res = await listPromises({ filter: filter.value, limit: PAGE_SIZE })
      if (seq !== requestSeq) return
      items.value = res.data
      nextCursor.value = res.nextCursor
      status.value = 'ready'
    } catch {
      if (seq !== requestSeq) return
      status.value = 'error'
    }
  }

  async function loadMore(): Promise<void> {
    if (!nextCursor.value || loadingMore.value) return
    const seq = requestSeq
    loadingMore.value = true
    try {
      const res = await listPromises({
        filter: filter.value,
        cursor: nextCursor.value,
        limit: PAGE_SIZE,
      })
      if (seq !== requestSeq) return
      items.value = [...items.value, ...res.data]
      nextCursor.value = res.nextCursor
    } catch {
      /* tlačítko zůstane, uživatel to zkusí znovu */
    } finally {
      loadingMore.value = false
    }
  }

  async function setFilter(next: PromiseListFilter): Promise<void> {
    if (filter.value === next && status.value === 'ready') return
    filter.value = next
    items.value = []
    nextCursor.value = null
    await load()
  }

  function forgetRelated(p: { clientId?: string | null; jobId?: string | null }): void {
    if (p.clientId) delete useClientsStore().details[p.clientId]
    if (p.jobId) delete useJobsStore().details[p.jobId]
  }

  /** Změní stav slibu v seznamu hned; patří-li do jiného filtru, zmizí. Vrátí funkci pro krok zpět. */
  function applyLocalStatus(id: string, next: PromiseItem['status']): () => void {
    const index = items.value.findIndex((p) => p.id === id)
    if (index < 0) return () => undefined
    const before = items.value[index]!
    const updated: PromiseItem = {
      ...before,
      status: next,
      doneAt: next === 'done' ? new Date().toISOString() : null,
      overdue: next === 'open' ? before.overdue : false,
    }
    if (belongs(filter.value, next)) items.value.splice(index, 1, updated)
    else items.value.splice(index, 1)
    return () => {
      const at = items.value.findIndex((p) => p.id === id)
      if (at >= 0) {
        items.value.splice(at, 1, before)
        return
      }
      items.value.splice(Math.min(index, items.value.length), 0, before)
    }
  }

  async function changeStatus(id: string, next: 'open' | 'done'): Promise<PromiseItem> {
    const rollback = applyLocalStatus(id, next)
    try {
      const saved = await updatePromise(id, { status: next })
      forgetRelated(saved)
      return saved
    } catch (e) {
      rollback()
      throw e
    }
  }

  /** Splněno jedním klepnutím: slib zmizí ze seznamu hned, při chybě se vrátí a chyba se předá dál. */
  function markDone(id: string): Promise<PromiseItem> {
    return changeStatus(id, 'done')
  }

  /** Vrátit splněný slib mezi otevřené. */
  function reopen(id: string): Promise<PromiseItem> {
    return changeStatus(id, 'open')
  }

  function fetchOne(id: string): Promise<PromiseItem> {
    return getPromise(id)
  }

  async function create(input: CreatePromiseInput): Promise<PromiseItem> {
    const saved = await createPromise(input)
    forgetRelated(saved)
    status.value = 'idle'
    return saved
  }

  async function update(id: string, input: UpdatePromiseInput): Promise<PromiseItem> {
    const before = items.value.find((p) => p.id === id)
    const saved = await updatePromise(id, input)
    forgetRelated(before ?? {})
    forgetRelated(saved)
    status.value = 'idle'
    return saved
  }

  async function remove(id: string): Promise<void> {
    const before = items.value.find((p) => p.id === id)
    await deletePromise(id)
    items.value = items.value.filter((p) => p.id !== id)
    forgetRelated(before ?? {})
  }

  /** Jiný uživatel nebo jiná firma = jiná data. */
  function invalidate(): void {
    requestSeq++
    items.value = []
    nextCursor.value = null
    status.value = 'idle'
    filter.value = 'open'
  }

  return {
    filter,
    items,
    nextCursor,
    status,
    loadingMore,
    load,
    loadMore,
    setFilter,
    markDone,
    reopen,
    fetchOne,
    create,
    update,
    remove,
    invalidate,
  }
})
